export class SearchAlgorithms {

    /**
    * Find the root of a continuous function using the robust and fast Brent algorithm
    * 
    * @param {number} a - The first starting value of the independent variable
    * @param {number} b - The second starting value of the independent variable
    * @param {number} x_tolerance - The tolerance in the independent variable, which determines when convergence is reached
    * @param func - The delegate function to compute the dependent variable
    * @param {number} fa0 - The value of func(a)
    * @param {number} fb0 - The value of func(b)
    * @returns the estimated value of a zero of the function
    */
    static findRootUsingBrentMethod(a: number, b: number, x_tolerance: number, func: (x: number) => number, fa0?: number, fb0?: number): number {

        //****************************************************************************80
        //
        //  Purpose:
        //
        //    seeks the root of a function F(X) in an interval [A,B].
        //
        //  Discussion:
        //
        //    The interval [A,B] must be a change of sign interval for F.
        //    That is, F(A) and F(B) must be of opposite signs.  Then
        //    assuming that F is continuous implies the existence of at least
        //    one value C between A and B for which F(C) = 0.
        //
        //    The location of the zero is determined to within an accuracy
        //    of 6 * MACHEPS * Math.abs ( C ) + 2 * T.
        //
        //    Thanks to Thomas Secretin for pointing out a transcription error in the
        //    setting of the value of P, 11 February 2013.
        //
        //  Licensing:
        //
        //    This code is distributed under the GNU LGPL license.
        //
        //  Modified:
        //
        //    11 February 2013
        //
        //  Author:
        //
        //    Original FORTRAN77 version by Richard Brent.
        //    C++ version by John Burkardt.
        //
        //  Reference:
        //
        //    Richard Brent,
        //    Algorithms for Minimization Without Derivatives,
        //    Dover, 2002,
        //    ISBN: 0-486-41998-3,
        //    LC: QA402.5.B74.
        //
        //  Parameters:
        //
        //    Input, double A, B, the endpoints of the change of sign interval.
        //
        //    Input, double T, a positive error tolerance.
        //
        //    Input, func_base& F, the name of a user-supplied c++ functor
        //    whose zero is being sought.  The input and output
        //    of F() are of type double.
        //
        //    Output, double ZERO, the estimated value of a zero of
        //    the function F.
        //

        let c, d, e, fa, fb, fc, m, macheps, p, q, r, s, sa, sb, tol;
        let iterations = 0;

        if (!fa0)
            fa0 = func(a);

        if (!fb0)
            fb0 = func(b);

        //
        //  Make local copies of A and B.
        //
        sa = a;
        sb = b;
        fa = fa0;
        fb = fb0;

        c = sa;
        fc = fa;
        e = sb - sa;
        d = e;

        //if (Number.EPSILON === undefined){
        //    Number.EPSILON  =  Math.pow(2, -52);
        macheps = Math.pow(2, -52);

        for (; ;) {
            ++iterations;

            if (Math.abs(fc) < Math.abs(fb)) {
                sa = sb;
                sb = c;
                c = sa;
                fa = fb;
                fb = fc;
                fc = fa;
            }

            tol = 2.0 * macheps * Math.abs(sb) + x_tolerance;
            m = 0.5 * (c - sb);

            if (Math.abs(m) <= tol || fb == 0.0)
                break;

            if (Math.abs(e) < tol || Math.abs(fa) <= Math.abs(fb)) {
                e = m;
                d = e;
            }
            else {
                s = fb / fa;

                if (sa == c) {
                    p = 2.0 * m * s;
                    q = 1.0 - s;
                }
                else {
                    q = fa / fc;
                    r = fb / fc;
                    p = s * (2.0 * m * q * (q - r) - (sb - sa) * (r - 1.0));
                    q = (q - 1.0) * (r - 1.0) * (s - 1.0);
                }

                if (0.0 < p)
                    q = -q;
                else
                    p = -p;

                s = e;
                e = d;

                if (2.0 * p < 3.0 * m * q - Math.abs(tol * q) && p < Math.abs(0.5 * s * q))
                    d = p / q;
                else {
                    e = m;
                    d = e;
                }
            }
            sa = sb;
            fa = fb;

            if (tol < Math.abs(d))
                sb = sb + d;
            else if (0.0 < m)
                sb = sb + tol;
            else
                sb = sb - tol;

            fb = func(sb);

            if ((0.0 < fb && 0.0 < fc) || (fb <= 0.0 && fc <= 0.0)) {
                c = sa;
                fc = fa;
                e = sb - sa;
                d = e;
            }
        }
        return sb;
    }

}


// WEBPACK FOOTER //
// ./src/SearchAlgorithms.ts